import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-env browser */
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { appendPath, getValue } from '@truqc/content-path';
import entityComposition from '../../../common/classes/entityComposition';
const styles = {
  markerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  mapboxglMarker: {
    backgroundImage: 'url(../../../../../img/map/map-marker-red.png)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    width: '30px',
    height: '30px',
    cursor: 'pointer'
  }
};
const mapTypes = {
  standard: 'mapbox://styles/mapbox/streets-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  hybrid: 'mapbox://styles/mapbox/satellite-streets-v11'
};
const MapView = _ref => {
  let contentPath = _ref.contentPath,
    _ref$form = _ref.form,
    form = _ref$form === void 0 ? {
      active: {
        content: {}
      }
    } : _ref$form,
    _ref$issue = _ref.issue,
    issue = _ref$issue === void 0 ? {
      active: {
        content: {}
      }
    } : _ref$issue,
    user = _ref.login.user,
    _ref$report = _ref.report,
    report = _ref$report === void 0 ? {
      active: {
        content: {}
      }
    } : _ref$report,
    parentContentPath = _ref.parentContentPath,
    _ref$rotateEnabled = _ref.rotateEnabled,
    rotateEnabled = _ref$rotateEnabled === void 0 ? false : _ref$rotateEnabled,
    _ref$scrollEnabled = _ref.scrollEnabled,
    scrollEnabled = _ref$scrollEnabled === void 0 ? true : _ref$scrollEnabled,
    _ref$showsCompass = _ref.showsCompass,
    showsCompass = _ref$showsCompass === void 0 ? false : _ref$showsCompass,
    _ref$showsScale = _ref.showsScale,
    showsScale = _ref$showsScale === void 0 ? false : _ref$showsScale,
    _ref$showsUserLocatio = _ref.showsUserLocation,
    showsUserLocation = _ref$showsUserLocatio === void 0 ? false : _ref$showsUserLocatio,
    _ref$zoomEnabled = _ref.zoomEnabled,
    zoomEnabled = _ref$zoomEnabled === void 0 ? true : _ref$zoomEnabled;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    latitude = _useState2[0],
    setLatitude = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    longitude = _useState4[0],
    setLongitude = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    mapType = _useState6[0],
    setMapType = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    sourceType = _useState8[0],
    setSourceType = _useState8[1];
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const tokenRef = useRef();
  const source = useMemo(() => {
    const candidate = [form, report, issue].find(obj => {
      var _obj$active;
      return obj === null || obj === void 0 || (_obj$active = obj.active) === null || _obj$active === void 0 ? void 0 : _obj$active.content;
    });
    if (candidate) {
      return candidate.active;
    }
    return null;
  }, [form, report, issue]);
  useEffect(() => {
    if (source) {
      setSourceType(source.entity);
    }
  }, [source]);
  const mapboxToken = user === null || user === void 0 ? void 0 : user.mapboxToken;
  if (mapboxToken) {
    tokenRef.current = mapboxToken;
  }
  const removeMarkers = () => {
    const map = mapInstanceRef.current;
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    if (!map) return;
    map.flyTo({
      center: [0, 0],
      zoom: 0,
      speed: 2
    });
  };
  const updateMarker = useCallback((lng, lat) => {
    const map = mapInstanceRef.current;
    if (!map || lng == null || lat == null) return;
    const markerContainer = document.createElement('div');
    Object.assign(markerContainer.style, styles.markerContainer);
    const markerElement = document.createElement('div');
    Object.assign(markerElement.style, styles.mapboxglMarker);
    markerContainer.appendChild(markerElement);
    const marker = new mapboxgl.Marker({
      element: markerContainer
    }).setLngLat([lng, lat]).addTo(map);
    map.flyTo({
      center: [lng, lat],
      zoom: 12,
      speed: 2
    });
    markersRef.current.push(marker);
  }, []);
  const updateMapStyle = useCallback((type, lng, lat) => {
    const map = mapInstanceRef.current;
    if (!map || !type) return;
    map.setStyle(type);
    map.once('styledata', () => {
      if (lng != null && lat != null) {
        updateMarker(lng, lat);
      }
    });
  }, [updateMarker]);
  useEffect(() => {
    var _markerLocation$latit, _markerLocation$longi, _mapTypes$typeKey;
    const _ref2 = getValue(appendPath(parentContentPath, contentPath), source.content) || {},
      _ref2$markerLocation = _ref2.markerLocation,
      markerLocation = _ref2$markerLocation === void 0 ? {} : _ref2$markerLocation,
      _ref2$mapType = _ref2.mapType,
      newMapType = _ref2$mapType === void 0 ? {} : _ref2$mapType;
    let typeKey = Object.keys(newMapType)[0] || 'standard';
    if (sourceType === 'issue') {
      const _ref3 = getValue(parentContentPath, source.content) || {},
        _ref3$mapType = _ref3.mapType,
        issueMapType = _ref3$mapType === void 0 ? {} : _ref3$mapType;
      if (issueMapType !== null && issueMapType !== void 0 && issueMapType.identifier) {
        typeKey = issueMapType.identifier;
      }
    }
    setLatitude((_markerLocation$latit = markerLocation.latitude) !== null && _markerLocation$latit !== void 0 ? _markerLocation$latit : null);
    setLongitude((_markerLocation$longi = markerLocation.longitude) !== null && _markerLocation$longi !== void 0 ? _markerLocation$longi : null);
    setMapType((_mapTypes$typeKey = mapTypes[typeKey]) !== null && _mapTypes$typeKey !== void 0 ? _mapTypes$typeKey : mapTypes.standard);
  }, [source, sourceType, contentPath, parentContentPath]);
  useEffect(() => {
    if (!mapContainerRef.current || !tokenRef.current) return;
    mapboxgl.accessToken = tokenRef.current;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapType || mapTypes.standard,
      center: longitude && latitude ? [longitude, latitude] : [0, 0],
      zoom: longitude && latitude ? 12 : 0,
      scrollZoom: zoomEnabled,
      dragPan: scrollEnabled
    });
    if (!rotateEnabled) {
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    }
    if (showsUserLocation && scrollEnabled) {
      // Add the GeolocateControl to show the user's location
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        showUserHeading: true
      });
      map.addControl(geolocateControl, 'top-right');
    }
    if (!zoomEnabled) {
      map.scrollZoom.disable();
      map.doubleClickZoom.disable();
    }
    if (showsCompass || zoomEnabled) {
      const navControl = new mapboxgl.NavigationControl({
        showCompass: showsCompass,
        showZoom: zoomEnabled
      });
      map.addControl(navControl, 'top-right');
    }
    if (showsScale) {
      const scaleControl = new mapboxgl.ScaleControl({
        maxWidth: 200,
        unit: 'imperial'
      });
      map.addControl(scaleControl, 'bottom-left');
    }
    mapInstanceRef.current = map;

    // eslint-disable-next-line consistent-return
    return () => {
      map.remove();
      removeMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapType) return;
    updateMapStyle(mapType, longitude, latitude);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapType, updateMapStyle]);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (longitude == null || latitude == null) {
      removeMarkers();
      return;
    }
    updateMarker(longitude, latitude);
  }, [longitude, latitude, updateMarker]);
  return tokenRef.current ? /*#__PURE__*/React.createElement("div", {
    "data-testid": "map-container",
    style: {
      width: '100%',
      height: '500px',
      position: 'relative'
    },
    ref: mapContainerRef
  }) : null;
};
MapView.displayName = 'Map';
MapView.propTypes = {
  contentPath: PropTypes.string.isRequired,
  form: PropTypes.shape({
    formTypeIdentifier: PropTypes.string,
    active: PropTypes.shape({
      content: PropTypes.shape({})
    })
  }),
  issue: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({})
    })
  }),
  login: PropTypes.shape({
    user: PropTypes.shape({
      mapboxToken: PropTypes.string
    })
  }).isRequired,
  parentContentPath: PropTypes.string.isRequired,
  report: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({})
    })
  }),
  rotateEnabled: PropTypes.bool,
  scrollEnabled: PropTypes.bool,
  showsCompass: PropTypes.bool,
  showsScale: PropTypes.bool,
  showsUserLocation: PropTypes.bool,
  zoomEnabled: PropTypes.bool
};
export { MapView as DisconnectedMapView };
export default entityComposition(MapView, ['form', 'report', 'issue', 'login'], {}, []);